import { DividerList, LabeledListItem } from '@mews-commander/ui';
import { getProductChargingTextKeyForBookableServices } from '@mews/business-components';
import { ProductChargingMode, UnicodeEntity } from '@mews/enums';
import { LocalizedText, LocalizedEnum, LocalizedCurrency, lt, TextKey } from '@mews/framework';
import { Stack, Tag, Typography } from '@optimus-web/core';
import type { ServiceCardProps } from '../types';

type ServiceCardDetailsProps = Pick<
    ServiceCardProps,
    'consumptionMoment' | 'options' | 'promotions' | 'adjustedPrice' | 'adjustment' | 'bookablePeriod' | 'charging'
>;

export const CardDetails = ({
    charging,
    bookablePeriod,
    consumptionMoment,
    options,
    promotions,
    adjustedPrice,
    adjustment,
}: ServiceCardDetailsProps) => {
    return (
        <DividerList>
            <LabeledListItem label={lt('ConfigureProductServicesPaymentsCharging')} data-test-id="productCharging">
                <Tag variant="basicSubtle" outline>
                    {/* TODO: Passed from previous implementation, requires fixing to avoid double casting -> PortfolioProductCharging === ProductChargingMode */}
                    <LocalizedText
                        textKey={getProductChargingTextKeyForBookableServices(
                            charging as unknown as ProductChargingMode,
                            bookablePeriod as unknown as TextKey
                        )}
                        substitutes={{ timeUnit: bookablePeriod || '' }}
                    />
                </Tag>
            </LabeledListItem>
            <LabeledListItem label={lt('ConfigureProductServicesPaymentsTiming')} data-test-id="productConsumption">
                {consumptionMoment ? (
                    <Tag variant="basicSubtle" outline>
                        <LocalizedEnum prefix="ProductConsumptionMoment" value={consumptionMoment} />
                    </Tag>
                ) : (
                    UnicodeEntity.Dash
                )}
            </LabeledListItem>
            <LabeledListItem
                label={lt('ProductOptions')}
                labelOptions={{ whiteSpace: 'nowrap' }}
                itemAlignment="start"
                data-test-id="productOptions"
            >
                {options?.length ? (
                    <Stack spacing="50" wrap>
                        {options.map((option) => (
                            <Tag key={option} variant="basicSubtle" outline>
                                <LocalizedEnum prefix="ProductOptions" value={option} />
                            </Tag>
                        ))}
                    </Stack>
                ) : (
                    UnicodeEntity.Dash
                )}
            </LabeledListItem>
            <LabeledListItem
                label={lt('ConfigureProductServicesPromotions')}
                labelOptions={{ whiteSpace: 'nowrap' }}
                itemAlignment="start"
                data-test-id="promotions"
            >
                {promotions?.length ? (
                    <Stack spacing="50" wrap>
                        {promotions.map((promotion) => (
                            <Tag key={promotion} variant="basicSubtle" outline>
                                <LocalizedEnum prefix="Promotions" value={promotion} />
                            </Tag>
                        ))}
                    </Stack>
                ) : (
                    UnicodeEntity.Dash
                )}
            </LabeledListItem>
            <LabeledListItem label={lt('GeneralAdjustedPriceTaxExcl')} data-test-id="adjustedPrice">
                <Stack>
                    <LocalizedCurrency value={adjustedPrice.netValue} currency={adjustedPrice.currency} />
                    {adjustment && (
                        <Typography textStyle="bodyLarge" variant="secondary" as="div">
                            {UnicodeEntity.NoBreakSpace}
                            {UnicodeEntity.LeftParenthesis}
                            <LocalizedEnum prefix="EnterpriseProductPriceAdjustment" value={adjustment.discriminator} />
                            {UnicodeEntity.RightParenthesis}
                        </Typography>
                    )}
                </Stack>
            </LabeledListItem>
        </DividerList>
    );
};
